import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import httpAdapterInstance from 'configs/HttpAdapterConfig';
import { EmployerApiEndpoints } from 'shared/ApiEndpoints';
import {
    TextMessageState,
    GetMessagesPayload,
    MarkAsReadPayload,
    GetMessagesResponse,
    SendMessagePayload,
} from './text-messages.types';
import { convertApiMessageToMessage } from './text-messages.helpers';

const initialState: TextMessageState = {
  messages: [],
  loadingTextMessagesStatus: 'idle',
  loadingTextMessagesResponse: '',
  sendingTextMessageStatus: 'idle',
  sendingTextMessageResponse: '',
  unreadCount: 0
};

export const sendMessage = createAsyncThunk(
  'textMessages/sendMessage',
  async (payload: SendMessagePayload, { rejectWithValue }) => {
    try {
      const response = await httpAdapterInstance.post(
        `${EmployerApiEndpoints.TEXT_MESSAGES}/send`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Send message error:', error);
      return rejectWithValue(error);
    }
  }
);

export const getMessages = createAsyncThunk('textMessages/getMessages',
  async (payload: GetMessagesPayload, { rejectWithValue }) => {
    return await httpAdapterInstance
      .get<GetMessagesResponse>(`${EmployerApiEndpoints.TEXT_MESSAGES}/${payload.candidateEmployerJobId}`)
      .then((response) => response?.data)
      .catch((error) => {
        throw rejectWithValue(error.response?.data?.error || 'Failed to fetch messages');
      });
  }
);

export const markAsRead = createAsyncThunk('textMessages/markAsRead',
  async (payload: MarkAsReadPayload, { rejectWithValue }) => {
    return await httpAdapterInstance
      .post(`${EmployerApiEndpoints.TEXT_MESSAGES}/${payload.candidateEmployerJobId}/read`)
      .then(() => payload.candidateEmployerJobId)
      .catch((error) => {
        throw rejectWithValue(error.response?.data?.error || 'Failed to mark messages as read');
      });
  }
);

const textMessagesSlice = createSlice({
  name: 'textMessages',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.messages = [];
      state.loadingTextMessagesStatus = 'idle';
      state.loadingTextMessagesResponse = null;
      state.sendingTextMessageStatus = 'idle';
      state.sendingTextMessageResponse = null;
      state.unreadCount = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.sendingTextMessageStatus = 'pending';
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingTextMessageStatus = 'success';
        if (action.payload) {
          const newMessage = convertApiMessageToMessage(action.payload);
          state.messages = [...state.messages, newMessage];
        }
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sendingTextMessageStatus = 'failed';
      })
      .addCase(getMessages.pending, (state) => {
        state.loadingTextMessagesStatus = 'pending';
        state.loadingTextMessagesResponse = null;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        state.loadingTextMessagesStatus = 'success';
        if (action.payload?.messages) {
          const sortedMessages = action.payload.messages
            .map(convertApiMessageToMessage)
            .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
          state.messages = sortedMessages;
          state.unreadCount = action.payload.unreadCount || 0;
        }
      })
      .addCase(getMessages.rejected, (state, action) => {
        state.loadingTextMessagesStatus = 'failed';
        state.loadingTextMessagesResponse = action.payload as string;
      })
      .addCase(markAsRead.fulfilled, (state) => {
        state.unreadCount = 0;
        state.messages = state.messages.map(message => ({
          ...message,
          readByEmployer: true
        }));
      });
  }
});

export const { clearMessages } = textMessagesSlice.actions;
export default textMessagesSlice.reducer; 